import React, { useContext } from 'react';
import {
  Image,
  ScrollView,
  StyleSheet,
  TouchableHighlight,
  View,
} from 'react-native';
import DyteText from './DyteText';

import DyteClient, { DytePlugin } from '@dytesdk/web-core';
import { UIConfig } from '../types/ui-config';
import { DyteI18n, useLanguage } from '../lib/lang';
import { defaultIconPack, IconPack } from '../lib/icons';
import { SvgXml } from 'react-native-svg';
import { defaultConfig } from '../lib/default-ui-config';
import { useDyteSelector } from '@dytesdk/react-native-core';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function DytePlugins({
  meeting,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config = defaultConfig,
  iconPack = defaultIconPack,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  size = 'sm',
  t,
}: {
  meeting: DyteClient;
  config?: UIConfig;
  iconPack?: IconPack;
  size?: 'lg' | 'md' | 'sm' | 'xl';
  t?: DyteI18n;
}) {
  const pluginsActive = useDyteSelector((m) => m.plugins.active);
  const pluginsAll = useDyteSelector((m) => m.plugins.all);
  const perms = useDyteSelector((m) => m.self.permissions);
  const presetEnabledPlugins = () => {
    return pluginsAll.toArray().filter((p) => {
      const pluginsConfig = perms.plugins.config;
      if (Object.entries(pluginsConfig).length <= 0) {
        return true;
      } else {
        for (let key in Object.entries(pluginsConfig)) {
          const conf = Object.entries(pluginsConfig)[key];
          if (conf[0] === p.id && conf[1]?.disabled) {
            return false;
          }
        }
      }
      return true;
    });
  };
  const pluginsAllowed = presetEnabledPlugins();
  const { storeStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const styles = StyleSheet.create({
    scrollContainer: {
      flex: 1,
      backgroundColor: colors.background[900],
    },
    renderContainer: {
      flex: 1,
      margin: 10,
    },
    text: {
      margin: 10,
      fontWeight: 'bold',
    },
    inactivePluginText: {
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    renderPluginContainer: {
      flex: 1,
      //height: 150,
      paddingHorizontal: 16,
      paddingVertical: 16,
    },
    pluginContent: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 10,
    },
    pluginContentWrapper: {
      flexDirection: 'row',
    },
    pluginName: {
      marginTop: 5,
    },
    iconButton: {
      padding: 10,
      backgroundColor: colors.background[800],
      borderRadius: 10,
    },
    image: {
      height: 40,
      width: 40,
      marginRight: 15,
    },
  });
  const renderPlugins = (plugins: DytePlugin[], type) => {
    const pArray = type === 'all' ? plugins.filter((p) => !p.active) : plugins;
    return pArray.map((p, index) => {
      return (
        <View style={styles.renderPluginContainer} key={index}>
          <View style={styles.pluginContent}>
            <View style={styles.pluginContentWrapper}>
              <Image style={styles.image} source={{ uri: p.picture }} />
              <DyteText style={styles.pluginName} size="lg">
                {p.name}
              </DyteText>
            </View>
            {((type === 'all' && meeting.self.permissions.plugins.canStart) ||
              (type !== 'all' &&
                meeting.self.permissions.plugins.canClose)) && (
              <TouchableHighlight
                underlayColor={colors.background[800]}
                disabled={
                  type === 'all'
                    ? !meeting.self.permissions.plugins.canStart
                    : !meeting.self.permissions.plugins.canClose
                }
                onPress={() => {
                  type === 'all' ? p.activate() : p.deactivate();
                }}
                style={{
                  ...styles.iconButton,
                }}
              >
                <SvgXml
                  xml={(type === 'all'
                    ? iconPack.rocket
                    : iconPack.dismiss
                  ).replace(
                    'currentColor',
                    type === 'all' ? colors.brand[300] : colors.danger
                  )}
                  width={23}
                  height={23}
                />
              </TouchableHighlight>
            )}
          </View>
          <View>
            <DyteText size="sm">{p.description}</DyteText>
          </View>
        </View>
      );
    });
  };
  return (
    <ScrollView style={styles.scrollContainer}>
      <View style={styles.renderContainer}>
        <DyteText size="lg" style={styles.text}>
          Active {t('plugins')}
        </DyteText>
        {pluginsActive.toArray().length !== 0 ? (
          renderPlugins(pluginsActive.toArray(), 'active')
        ) : (
          <View style={styles.inactivePluginText}>
            <DyteText size="sm">No active plugin</DyteText>
            <DyteText size="sm">activate one below 👇🏽</DyteText>
          </View>
        )}
      </View>
      <View style={styles.renderContainer}>
        <DyteText size="lg" style={styles.text}>
          Plugin Store
        </DyteText>
        {renderPlugins(pluginsAllowed, 'all')}
      </View>
    </ScrollView>
  );
}
